import { guardarDestino, recogerDestino } from '../lib/destino.js';
import { PLANS, isUnlimited } from '@qr/shared/plans';
import { Button, Field } from '@qr/ui';
import { AnimatePresence, motion } from 'motion/react';
import {
  ArrowLeft,
  CheckCircle2,
  ExternalLink,
  KeyRound,
  MailCheck,
  RefreshCw,
  ShieldCheck,
  Zap,
} from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import { messageFromError } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';

/**
 * Pantalla de acceso.
 *
 * Una sola cosa que hacer: escribir el correo. Sin contraseña y sin registro
 * aparte — el mismo formulario sirve para entrar y para darse de alta, y quien
 * llega no tiene que decidir en cuál de los dos está.
 *
 * Eso, que simplifica muchísimo, tiene un precio: el momento en que la persona
 * deja esta pantalla y se va a su correo es donde se pierde la mitad de la
 * gente. Por eso el trabajo de verdad no está en el formulario —que es un
 * campo— sino en la pantalla de después: decir a qué dirección exacta ha ido,
 * llevar al buzón de un toque, avisar de la carpeta de spam antes de que
 * pregunten, y dejar reenviar sin que parezca que algo falló.
 */

/** Espera antes de poder pedir otro enlace. */
const ESPERA_REENVIO = 30;

/**
 * El buzón de quien acaba de escribir su correo.
 *
 * Llevarle a su bandeja de un toque quita el único paso incómodo del acceso
 * sin contraseña: salir de aquí, encontrar la aplicación de correo y volver.
 * Solo para los tres proveedores que cubren a casi todo el mundo; para el
 * resto no se inventa nada y se dice simplemente que mire su correo.
 */
function buzonDe(email: string): { nombre: string; url: string } | null {
  const dominio = email.split('@')[1]?.toLowerCase() ?? '';
  if (/^(gmail|googlemail)\.com$/.test(dominio)) {
    return { nombre: 'Gmail', url: 'https://mail.google.com' };
  }
  if (/^(outlook|hotmail|live|msn)\./.test(dominio)) {
    return { nombre: 'Outlook', url: 'https://outlook.live.com/mail' };
  }
  if (/^(yahoo)\./.test(dominio)) {
    return { nombre: 'Yahoo', url: 'https://mail.yahoo.com' };
  }
  return null;
}

/**
 * Lo que trae el plan gratuito, sacado del plan y no escrito a mano.
 *
 * Esta frase decia "tres códigos y cinco mil escaneos" mucho despues de que
 * el gratuito pasara a uno y mil. Una cifra copiada a mano en una pantalla
 * que casi nadie vuelve a mirar envejece sin que nadie se entere, y aqui
 * encima es una promesa: la lee alguien justo antes de darse de alta.
 */
function loQueTraeElGratis(): string {
  const l = PLANS.free.limits;
  const codigos = l.codes === 1 ? 'Un código' : `${l.codes} códigos`;
  const escaneos = isUnlimited(l.scansPerMonth)
    ? 'escaneos sin límite'
    : `${l.scansPerMonth.toLocaleString('es-CO')} escaneos al mes`;
  return `${codigos} y ${escaneos}, sin tarjeta.`;
}

const PROMESAS = [
  {
    Icono: KeyRound,
    que: 'Sin contraseñas',
    como: 'Un enlace al correo y dentro. Nada que recordar ni que te roben.',
  },
  {
    Icono: Zap,
    que: 'Gratis para empezar',
    como: loQueTraeElGratis(),
  },
  {
    Icono: ShieldCheck,
    que: 'Lo impreso no caduca',
    como: 'Cambias el destino cuando quieras; el papel sigue valiendo.',
  },
];

export function SignIn() {
  const { user, loading, sendLink, signInWithGoogle, pendingEmail } = useAuth();

  // Si ya pidió un enlace antes desde este navegador, su correo está guardado.
  // Volver a escribirlo entero es trabajo que ya hizo.
  const [email, setEmail] = useState(pendingEmail ?? '');
  const [enviando, setEnviando] = useState(false);
  const [enviadoA, setEnviadoA] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [espera, setEspera] = useState(0);
  const [conGoogle, setConGoogle] = useState(false);

  useEffect(() => {
    if (espera <= 0) return;
    const t = setTimeout(() => setEspera((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [espera]);

  /*
   * A donde queria ir quien llego aqui rebotado.
   *
   * Se guarda nada mas pisar la pantalla y no al entrar, porque con el enlace
   * magico esta pestana puede no ser la que termine el acceso: el usuario abre
   * su correo y vuelve por otra. Guardado en el navegador, llega igual.
   */
  const [parametros] = useSearchParams();
  const rutaPedida = parametros.get('destino');
  useEffect(() => {
    guardarDestino(rutaPedida);
  }, [rutaPedida]);

  if (loading) return null;
  /*
   * Con sesion ya abierta, derecho a lo que venia a hacer.
   *
   * Cubre los dos casos a la vez: quien ya estaba dentro y elige un plan en la
   * portada, y quien acaba de entrar con Google —ahi la ventana vuelve sola y
   * este mismo `Navigate` se encarga—.
   */
  if (user) return <Navigate to={recogerDestino() ?? '/'} replace />;

  async function entrarConGoogle() {
    setError(null);
    setConGoogle(true);
    try {
      // No se navega a ningun sitio al terminar: en cuanto Firebase tiene
      // sesion, el `<Navigate>` de arriba se encarga. Hacerlo a mano aqui
      // provocaria dos navegaciones peleandose.
      await signInWithGoogle();
    } catch (caught) {
      setError(messageFromError(caught));
    } finally {
      setConGoogle(false);
    }
  }

  async function enviar(destino: string) {
    setError(null);
    setEnviando(true);
    try {
      await sendLink(destino);
      setEnviadoA(destino.trim().toLowerCase());
      setEspera(ESPERA_REENVIO);
    } catch (caught) {
      setError(messageFromError(caught));
    } finally {
      setEnviando(false);
    }
  }

  function alEnviar(event: FormEvent) {
    event.preventDefault();
    const limpio = email.trim();
    // Una comprobación mínima aquí ahorra el viaje y un error del servidor que
    // suena mucho peor de lo que es.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(limpio)) {
      setError('Revisa la dirección: parece que le falta algo.');
      return;
    }
    void enviar(limpio);
  }

  return (
    <main className="grid min-h-dvh grid-cols-1 bg-brand-canvas lg:grid-cols-2">
      {/*
        El panel de la izquierda solo existe en pantalla ancha. En un móvil
        sobra: ahí quien llega ya decidió entrar, y lo que necesita es el campo
        cuanto antes, no que le vuelvan a vender el producto.

        Su contenido va centrado en bloque y no repartido por las esquinas: en
        un monitor de 2560 el ancho del panel es enorme, y una marca arriba del
        todo con un pie abajo del todo deja media pantalla de vacío entre dos
        cosas que no se miran juntas.
      */}
      {/*
        El mismo índigo de la portada, no una superficie del panel.

        Esta pantalla es la costura entre la web que convence y la herramienta
        que se usa: quien llega aquí viene de ver el cartel de la portada, y
        encontrarse un gris de panel la primera vez rompe la continuidad justo
        en el peor momento. De ahí que repita el titular, el subrayado en lima
        y el logotipo tal cual están allí.
      */}
      <aside className="relative hidden items-center justify-end overflow-hidden py-14 pe-10 ps-10 text-on-brand lg:flex xl:pe-16 xl:ps-20">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-48 -left-32 size-[40rem] rounded-full bg-accent opacity-[0.1] blur-3xl"
        />

        <div className="relative flex w-full max-w-[34rem] flex-col gap-9">
          <span className="logotipo w-[132px] text-accent" role="img" aria-label="Apunta QR" />

          {/*
            En Mosow y con la palabra en lima, igual que en la portada. Es el
            único sitio del panel con letra lo bastante grande para que el
            trazo de esta tipografía se lea como carácter.
          */}
          <h2 className="m-0 font-display text-[2.4rem] leading-[1.08] font-bold tracking-tight text-balance xl:text-[3rem]">
            <span className="rounded-full bg-accent px-[0.28em] pb-[0.06em] text-accent-ink">
              Imprime
            </span>{' '}
            una vez.
            <br />
            Cambia cuando quieras.
          </h2>

          <ul className="flex list-none flex-col gap-5 p-0">
            {PROMESAS.map(({ Icono, que, como }) => (
              <li key={que} className="grid grid-cols-[auto_1fr] items-start gap-3.5">
                <span className="grid size-9 place-items-center rounded-lg bg-white/15 text-accent">
                  <Icono size={17} strokeWidth={1.8} aria-hidden />
                </span>
                <span className="flex flex-col gap-0.5">
                  <strong className="text-[14.5px] font-semibold">{que}</strong>
                  <span className="text-[13px] leading-relaxed text-on-brand-2">
                    {como}
                  </span>
                </span>
              </li>
            ))}
          </ul>

          <p className="m-0 border-t border-white/20 pt-5 text-[12.5px] text-on-brand-2">
            <a href="https://apuntaqr.com" className="text-on-brand-2 no-underline hover:text-on-brand">
              apuntaqr.com
            </a>{' '}
            · códigos QR que puedes cambiar después de imprimir
          </p>
        </div>
      </aside>

      <div className="flex items-center justify-center px-5 py-10 sm:px-10 lg:justify-start lg:ps-10 xl:ps-20">
        <div className="flex w-full max-w-[26rem] flex-col gap-6 rounded-2xl border border-line bg-surface p-7 shadow-[0_34px_70px_-30px_rgb(8_12_50/0.7)] sm:p-8">
          <div className="lg:hidden">
            <Marca />
          </div>

          {/*
            El cambio entre pedir el correo y "ya te lo mandamos" se anima
            porque es un cambio de estado, no una entrada: la animación dice
            que algo pasó. Se monta uno cuando el otro ya se ha ido, para que
            no se solapen dos columnas de texto.
          */}
          <AnimatePresence mode="wait" initial={false}>
            {enviadoA ? (
              <motion.div
                key="enviado"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.22, ease: 'easeOut' }}
                className="flex flex-col gap-5"
              >
                <Enviado
                  email={enviadoA}
                  espera={espera}
                  enviando={enviando}
                  error={error}
                  onReenviar={() => void enviar(enviadoA)}
                  onOtroCorreo={() => {
                    setEnviadoA(null);
                    setError(null);
                    setEspera(0);
                  }}
                />
              </motion.div>
            ) : (
              <motion.div
                key="formulario"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.22, ease: 'easeOut' }}
                className="flex flex-col gap-5"
              >
                <header className="flex flex-col gap-2">
                  <h1 className="m-0 text-2xl font-bold tracking-tight">
                    Entra en tu cuenta
                  </h1>
                  <p className="m-0 text-[14.5px] leading-relaxed text-ink-2">
                    Escribe tu correo y te mandamos un enlace para entrar.{' '}
                    <strong className="font-semibold text-ink">
                      Aquí no hay contraseñas.
                    </strong>
                  </p>
                </header>

                {/*
                  Google primero, y el correo debajo.

                  No es una preferencia estética: entrar con Google es un clic
                  y el enlace por correo son cuatro pasos y un salto a otra
                  aplicación. El orden de la pantalla debe ser el orden de
                  facilidad, no el de lo que construimos antes.
                */}
                <button
                  type="button"
                  className="flex min-h-[46px] w-full items-center justify-center gap-2.5 rounded-DEFAULT border border-line bg-surface px-4 text-[14.5px] font-semibold text-ink transition-colors hover:bg-raise disabled:opacity-60"
                  onClick={() => void entrarConGoogle()}
                  disabled={conGoogle}
                >
                  <LogoGoogle />
                  {conGoogle ? 'Abriendo Google...' : 'Continuar con Google'}
                </button>

                <div className="flex items-center gap-3 text-[12px] text-ink-3">
                  <span className="h-px flex-1 bg-line" />o con tu correo
                  <span className="h-px flex-1 bg-line" />
                </div>

                <form onSubmit={alEnviar} className="flex flex-col gap-4" noValidate>
                  <Field
                    label="Correo electrónico"
                    type="email"
                    name="email"
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value);
                      if (error) setError(null);
                    }}
                    autoComplete="email"
                    inputMode="email"
                    autoFocus
                    required
                    placeholder="tu@empresa.com"
                    error={error}
                  />
                  <Button
                    type="submit"
                    loading={enviando}
                    disabled={email.trim() === ''}
                  >
                    Enviarme el enlace
                  </Button>
                </form>

                <p className="m-0 text-[12.5px] leading-relaxed text-ink-3">
                  Si es tu primera vez, se crea una cuenta con este correo. Al entrar
                  aceptas los{' '}
                  <a className="underline underline-offset-2 hover:text-ink-2" href="/terminos">
                    términos
                  </a>{' '}
                  y la{' '}
                  <a className="underline underline-offset-2 hover:text-ink-2" href="/privacidad">
                    política de privacidad
                  </a>
                  .
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </main>
  );
}

function Marca() {
  return (
    <a
      href="https://apuntaqr.com"
      className="block w-fit text-ink no-underline"
      aria-label="Apunta QR"
    >
      <span className="logotipo w-[124px]" role="img" aria-label="Apunta QR" />
    </a>
  );
}

/**
 * La pantalla de después, que es la que de verdad importa.
 *
 * Aquí la persona se va de la aplicación a buscar un correo, y cada duda que
 * se lleve es una que no vuelve: si llegó o no, a qué dirección, cuánto
 * esperar, qué hacer si no aparece. Todas contestadas antes de que las haga.
 */
function Enviado({
  email,
  espera,
  enviando,
  error,
  onReenviar,
  onOtroCorreo,
}: {
  readonly email: string;
  readonly espera: number;
  readonly enviando: boolean;
  readonly error: string | null;
  readonly onReenviar: () => void;
  readonly onOtroCorreo: () => void;
}) {
  const buzon = buzonDe(email);

  return (
    <>
      <header className="flex flex-col gap-3">
        <span
          aria-hidden
          className="grid size-12 place-items-center rounded-xl bg-ok-soft text-ok"
        >
          <MailCheck size={23} strokeWidth={1.8} />
        </span>
        <h1 className="m-0 text-2xl font-bold tracking-tight">Mira tu correo</h1>
        <p className="m-0 text-[14.5px] leading-relaxed text-ink-2">
          Hemos mandado un enlace de acceso a esta dirección. Ábrelo y entras directo.
        </p>
        {/*
          La dirección en su propio renglón y no dentro de la frase.
          Encajada en el párrafo se partía a mitad de palabra —"...@gmai /
          l.com"— y justo esta línea es la que hay que poder leer de un
          vistazo: la mitad de los "no me llegó" son una letra mal escrita
          aquí, y para verlo hay que verla entera.
        */}
        <p className="m-0 rounded-DEFAULT border border-line bg-surface px-3.5 py-2.5 text-[14px] font-semibold [overflow-wrap:anywhere] text-ink">
          {email}
        </p>
      </header>

      {buzon ? (
        <a
          className="inline-flex items-center justify-center gap-2 rounded-DEFAULT bg-brand px-4 py-2.5 text-[14px] font-semibold text-brand-ink no-underline transition-colors hover:bg-brand-strong"
          href={buzon.url}
          target="_blank"
          rel="noopener noreferrer"
        >
          Abrir {buzon.nombre}
          <ExternalLink size={15} aria-hidden />
        </a>
      ) : null}

      <ul className="m-0 flex list-none flex-col gap-2.5 rounded-lg border border-line bg-surface p-4">
        {[
          'El enlace caduca pronto y solo sirve una vez.',
          'Ábrelo en este mismo dispositivo para entrar sin escribir nada más.',
          'Si no aparece en un minuto, míralo en la carpeta de correo no deseado.',
        ].map((linea) => (
          <li key={linea} className="grid grid-cols-[auto_1fr] items-start gap-2.5">
            <CheckCircle2
              size={15}
              className="mt-0.5 shrink-0 text-ink-3"
              aria-hidden
            />
            <span className="text-[13px] leading-relaxed text-ink-2">{linea}</span>
          </li>
        ))}
      </ul>

      {error ? (
        <p className="m-0 text-[13px] text-bad" role="alert">
          {error}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        {/*
          Reenviar con una espera de por medio. Sin ella, quien no ve el correo
          al segundo pulsa cinco veces, se manda cinco enlaces —y solo vale el
          ultimo—, con lo que el que abra puede ser justo uno de los muertos.
        */}
        <Button
          variant="secondary"
          size="sm"
          onClick={onReenviar}
          loading={enviando}
          disabled={espera > 0}
        >
          <RefreshCw size={14} aria-hidden />
          {espera > 0 ? `Reenviar en ${espera}s` : 'Reenviar el enlace'}
        </Button>
        <Button variant="ghost" size="sm" onClick={onOtroCorreo}>
          <ArrowLeft size={14} aria-hidden />
          Usar otro correo
        </Button>
      </div>
    </>
  );
}

/**
 * El logotipo de Google, dibujado.
 *
 * Con sus cuatro colores y no en monocromo: las condiciones de uso de la marca
 * lo exigen, y ademas es lo que hace que el boton se reconozca sin leerlo.
 */
function LogoGoogle() {
  return (
    <svg width="17" height="17" viewBox="0 0 18 18" aria-hidden>
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58C13.46.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"
      />
    </svg>
  );
}
